import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, CardContent } from '../../../components/ui';
import { useLanguageStore } from '../../../store/languageStore';
import { getUITranslation } from '../i18n/translations';
import { createRoom as createRoomAPI } from '../services/api';

export const CreateRoom = () => {
  const navigate = useNavigate();
  const { language } = useLanguageStore();
  const t = getUITranslation(language);

  const [formData, setFormData] = useState({
    name: '',
    adminName: '',
    email: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await createRoomAPI({
        name: formData.name,
        adminName: formData.adminName,
        email: formData.email,
      });

      sessionStorage.setItem('userEmail', formData.email);
      sessionStorage.setItem('adminToken', response.adminToken);
      sessionStorage.setItem('roomId', response.roomId);

      try {
        const decodedToken = atob(response.adminToken);
        const tokenParts = decodedToken.split(':');
        const userId = tokenParts[1] || response.roomId;
        sessionStorage.setItem('userId', userId);
      } catch (e) {
        console.error('Failed to decode token:', e);
        sessionStorage.setItem('userId', response.roomId);
      }

      navigate(`/apps/kanban-board/room/${response.roomId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create room');
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="container mx-auto px-4 py-12 md:py-16">
      <div className="mx-auto max-w-3xl">
        <section className="mb-10 text-center">
          <div className="mb-4 text-5xl">📋</div>
          <h1 className="mb-3 text-4xl font-bold md:text-5xl">
            <span className="bg-gradient-to-r from-primary via-purple-500 to-pink-500 bg-clip-text text-transparent">
              {t.createRoom.title}
            </span>
          </h1>
          <p className="mx-auto max-w-2xl text-base text-muted-foreground md:text-lg">
            {t.createRoom.subtitle}
          </p>
        </section>

        <Card className="relative overflow-hidden border-2 bg-gradient-to-br from-primary/10 via-transparent to-secondary/10">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent" />
          <CardContent className="relative p-8 md:p-10">
            {error && (
              <div className="mb-6 rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="name" className="mb-2 block text-sm font-semibold">
                  {t.createRoom.roomName}
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t.createRoom.roomNamePlaceholder}
                  required
                  disabled={loading}
                  className="h-11 w-full rounded-lg border border-border/80 bg-background/80 px-4 transition-colors focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                />
              </div>

              <div>
                <label htmlFor="adminName" className="mb-2 block text-sm font-semibold">
                  {t.createRoom.playerName}
                </label>
                <input
                  type="text"
                  id="adminName"
                  name="adminName"
                  value={formData.adminName}
                  onChange={handleChange}
                  placeholder={t.createRoom.playerNamePlaceholder}
                  required
                  disabled={loading}
                  className="h-11 w-full rounded-lg border border-border/80 bg-background/80 px-4 transition-colors focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                />
              </div>

              <div>
                <label htmlFor="email" className="mb-2 block text-sm font-semibold">
                  {t.createRoom.email}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t.createRoom.emailPlaceholder}
                  required
                  disabled={loading}
                  className="h-11 w-full rounded-lg border border-border/80 bg-background/80 px-4 transition-colors focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                />
              </div>

              <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:gap-4">
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  className="flex-1"
                  onClick={() => navigate('/apps/kanban-board')}
                  disabled={loading}
                >
                  {t.createRoom.cancel}
                </Button>
                <Button type="submit" size="lg" className="flex-1" disabled={loading}>
                  {loading ? 'Creating...' : t.createRoom.create}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
